import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Save, DollarSign, Megaphone, TrendingUp, Users } from 'lucide-react';
import { adminPlanStats, ownerUpsertPlan, ownerDeletePlan, ownerUpsertAdPackage, ownerDeleteAdPackage } from '../lib/api';

export default function AdminPlansTab({ isOwner }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setData(await adminPlanStats()); }
    catch { toast.error('Could not load plans'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex items-center gap-2 text-white/60 py-16 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  if (!data) return null;

  return (
    <div data-testid="admin-plans-tab">
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Tile icon={Users}      color="#22d3ee" label="Total users"   value={data.totals.total_users} sub={`${data.totals.paid_users} paying`} />
        <Tile icon={DollarSign} color="#34d399" label="MRR"           value={'$' + data.totals.mrr_usd.toFixed(2)} />
        <Tile icon={TrendingUp} color="#a78bfa" label="ARR"           value={'$' + data.totals.arr_usd.toFixed(2)} />
        <Tile icon={Megaphone}  color="#ffcf52" label="Ad revenue (lifetime)" value={'$' + data.totals.ad_revenue_lifetime_usd.toFixed(2)} />
      </section>

      <PlansList plans={data.plans} isOwner={isOwner} onChanged={load} />

      <AdPackagesList pkgs={data.ad_packages} isOwner={isOwner} onChanged={load} />
    </div>
  );
}

function Tile({ icon: Ic, color, label, value, sub }) {
  return (
    <div className="card-shell p-4">
      <div className="w-9 h-9 rounded-lg grid place-items-center" style={{ background: color + '22', color }}><Ic className="w-4 h-4" /></div>
      <div className="mt-2 font-display text-[24px] leading-none">{value}</div>
      <div className="text-[11.5px] text-white/55 mt-1">{label}{sub && <span className="text-white/35"> · {sub}</span>}</div>
    </div>
  );
}

function PlansList({ plans, isOwner, onChanged }) {
  const [editing, setEditing] = useState(null);
  return (
    <section className="mb-7">
      <div className="flex items-center justify-between mb-2">
        <h3 className="row-title text-white">Subscription plans</h3>
        {isOwner && (
          <button onClick={() => setEditing({ id: '', name: '', price: 0, storage_mb: 1024, tagline: '' })} className="btn-ghost text-[12px]" data-testid="admin-plan-new">
            <Plus className="w-3.5 h-3.5" /> Add plan
          </button>
        )}
      </div>
      <div className="card-shell overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-white/[0.03] text-white/55 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">Plan</th>
              <th className="text-left px-3 py-2.5 hidden sm:table-cell">Price/mo</th>
              <th className="text-left px-3 py-2.5 hidden md:table-cell">Storage</th>
              <th className="text-left px-3 py-2.5">Subs</th>
              <th className="text-left px-3 py-2.5 hidden md:table-cell">MRR</th>
              <th className="px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {plans.map(p => (
              <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`admin-plan-row-${p.id}`}>
                <td className="px-4 py-2.5"><div className="font-semibold">{p.name}</div><div className="text-[11px] text-white/45">{p.tagline}</div></td>
                <td className="px-3 py-2.5 hidden sm:table-cell">${(p.price || 0).toFixed(2)}</td>
                <td className="px-3 py-2.5 hidden md:table-cell">{(p.storage_mb / 1024).toFixed(0)} GB</td>
                <td className="px-3 py-2.5 font-semibold">{p.subscribers || 0}</td>
                <td className="px-3 py-2.5 hidden md:table-cell text-[#34d399]">${(p.mrr_usd || 0).toFixed(2)}</td>
                <td className="px-3 py-2.5 text-right">
                  {isOwner && <button onClick={() => setEditing(p)} className="text-[11.5px] text-white/65 hover:text-white" data-testid={`admin-plan-edit-${p.id}`}>Edit</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <PlanEditor plan={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged(); }} />}
    </section>
  );
}

function PlanEditor({ plan, onClose, onSaved }) {
  const [form, setForm] = useState({ ...plan });
  const [saving, setSaving] = useState(false);
  const isNew = !plan.id;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.id || !form.name) { toast.error('id + name required'); return; }
    setSaving(true);
    try {
      await ownerUpsertPlan(form.id, { id: form.id, name: form.name, price: parseFloat(form.price) || 0, storage_mb: parseInt(form.storage_mb, 10) || 0, tagline: form.tagline || '' });
      toast.success('Saved');
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };
  const remove = async () => {
    if (!window.confirm(`Delete plan "${form.name}"?`)) return;
    try { await ownerDeletePlan(form.id); toast.success('Deleted'); onSaved(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Delete failed'); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-3" onClick={onClose}>
      <div className="card-shell w-full max-w-[480px] p-5" onClick={e => e.stopPropagation()} data-testid="admin-plan-editor">
        <h3 className="font-display text-[20px] mb-3">{isNew ? 'New plan' : 'Edit plan'}</h3>
        {isNew && <Inp label="ID (slug)" value={form.id} onChange={v => set('id', v)} placeholder="plus" />}
        <Inp label="Name" value={form.name} onChange={v => set('name', v)} placeholder="Plus" />
        <Inp label="Price (USD/mo)" value={form.price} onChange={v => set('price', v)} type="number" />
        <Inp label="Storage (MB)" value={form.storage_mb} onChange={v => set('storage_mb', v)} type="number" />
        <Inp label="Tagline" value={form.tagline} onChange={v => set('tagline', v)} placeholder="For serious creators" />
        <div className="flex justify-between mt-4">
          {!isNew ? <button onClick={remove} className="btn-ghost text-[12px]" style={{ color: '#ff6b8a' }}><Trash2 className="w-3.5 h-3.5" /> Delete</button> : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-ghost text-[12px]">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-primary text-[12px]" data-testid="admin-plan-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AdPackagesList({ pkgs, isOwner, onChanged }) {
  const [editing, setEditing] = useState(null);
  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h3 className="row-title text-white">Ad packages</h3>
        {isOwner && <button onClick={() => setEditing({ id: '', name: '', placement: 'home_banner', price: 0, days: 7, impressions: 0 })} className="btn-ghost text-[12px]" data-testid="admin-pkg-new"><Plus className="w-3.5 h-3.5" /> Add package</button>}
      </div>
      <div className="card-shell overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-white/[0.03] text-white/55 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">Package</th>
              <th className="text-left px-3 py-2.5 hidden sm:table-cell">Placement</th>
              <th className="text-left px-3 py-2.5">Price</th>
              <th className="text-left px-3 py-2.5 hidden md:table-cell">Days</th>
              <th className="text-left px-3 py-2.5">Sold</th>
              <th className="text-left px-3 py-2.5 hidden md:table-cell">Revenue</th>
              <th className="px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {pkgs.map(p => (
              <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`admin-pkg-row-${p.id}`}>
                <td className="px-4 py-2.5 font-semibold">{p.name}</td>
                <td className="px-3 py-2.5 hidden sm:table-cell text-white/65">{p.placement}</td>
                <td className="px-3 py-2.5">${(p.price || 0).toFixed(0)}</td>
                <td className="px-3 py-2.5 hidden md:table-cell">{p.days}</td>
                <td className="px-3 py-2.5">{p.paid_count || 0}</td>
                <td className="px-3 py-2.5 hidden md:table-cell text-[#34d399]">${(p.revenue_usd || 0).toFixed(2)}</td>
                <td className="px-3 py-2.5 text-right">
                  {isOwner && <button onClick={() => setEditing(p)} className="text-[11.5px] text-white/65 hover:text-white" data-testid={`admin-pkg-edit-${p.id}`}>Edit</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <AdPkgEditor pkg={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged(); }} />}
    </section>
  );
}

function AdPkgEditor({ pkg, onClose, onSaved }) {
  const [form, setForm] = useState({ ...pkg });
  const [saving, setSaving] = useState(false);
  const isNew = !pkg.id;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.id || !form.name) { toast.error('id + name required'); return; }
    setSaving(true);
    try {
      await ownerUpsertAdPackage(form.id, {
        id: form.id, name: form.name, placement: form.placement,
        price: parseFloat(form.price) || 0, days: parseInt(form.days, 10) || 7,
        impressions: parseInt(form.impressions, 10) || 0,
      });
      toast.success('Saved'); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };
  const remove = async () => {
    if (!window.confirm(`Delete package "${form.name}"?`)) return;
    try { await ownerDeleteAdPackage(form.id); toast.success('Deleted'); onSaved(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Delete failed'); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-3" onClick={onClose}>
      <div className="card-shell w-full max-w-[480px] p-5" onClick={e => e.stopPropagation()} data-testid="admin-pkg-editor">
        <h3 className="font-display text-[20px] mb-3">{isNew ? 'New ad package' : 'Edit ad package'}</h3>
        {isNew && <Inp label="ID (slug)" value={form.id} onChange={v => set('id', v)} placeholder="hero-7d" />}
        <Inp label="Name" value={form.name} onChange={v => set('name', v)} />
        <Inp label="Placement" value={form.placement} onChange={v => set('placement', v)} placeholder="home_banner" />
        <Inp label="Price (USD)" value={form.price} onChange={v => set('price', v)} type="number" />
        <Inp label="Days" value={form.days} onChange={v => set('days', v)} type="number" />
        <Inp label="Impression cap" value={form.impressions} onChange={v => set('impressions', v)} type="number" />
        <div className="flex justify-between mt-4">
          {!isNew ? <button onClick={remove} className="btn-ghost text-[12px]" style={{ color: '#ff6b8a' }}><Trash2 className="w-3.5 h-3.5" /> Delete</button> : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-ghost text-[12px]">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-primary text-[12px]" data-testid="admin-pkg-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Inp({ label, value, onChange, type = 'text', placeholder }) {
  return (
    <label className="block mb-3">
      <div className="text-[11px] uppercase tracking-wider font-bold text-white/55 mb-1.5">{label}</div>
      <input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="input-base" />
    </label>
  );
}
